import { useState, useCallback } from 'react';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import { compressImage, IMAGE_PRESETS } from '../lib/imageUtils';

const MAX_EVIDENCE_IMAGES = 5;

/**
 * Dispute Evidence Upload Hook
 *
 * Handles uploading evidence images to Firebase Storage with compression.
 * Storage path: disputes/{disputeId}/{timestamp}-{filename}.jpg
 *
 * Since disputeId may not exist yet when raising a dispute, we use a
 * temporary unique ID. The URLs are stored in the dispute document.
 */
export function useDisputeEvidence() {
  const { user } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [evidenceUrls, setEvidenceUrls] = useState([]);
  const [error, setError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState({});

  /**
   * Upload a single evidence image
   *
   * @param {File} file - The file to upload
   * @param {string} disputeId - Optional dispute ID (generates temp ID if not provided)
   * @param {function} onProgress - Progress callback (0-100)
   * @returns {Promise<string>} - The download URL
   */
  const uploadSingleEvidence = useCallback(
    async (file, disputeId, onProgress) => {
      if (!user) {
        throw new Error('Must be logged in to upload');
      }

      // Use provided disputeId or generate a temporary one based on user + timestamp
      const pathId = disputeId || `pending-${user.uid}-${Date.now()}`;

      // Compress image
      const compressedFile = await compressImage(file, IMAGE_PRESETS.full);

      // Generate unique filename
      const timestamp = Date.now();
      const baseName = file.name.replace(/\.[^.]+$/, '').replace(/[^a-zA-Z0-9.-]/g, '_');
      const filePath = `disputes/${pathId}/${timestamp}-${baseName}.jpg`;

      console.log(
        '[useDisputeEvidence] Original:',
        file.name,
        (file.size / 1024).toFixed(0) + 'KB',
        '-> Compressed:',
        (compressedFile.size / 1024).toFixed(0) + 'KB'
      );

      const storageRef = ref(storage, filePath);

      return new Promise((resolve, reject) => {
        const uploadTask = uploadBytesResumable(storageRef, compressedFile, {
          contentType: compressedFile.type,
        });

        uploadTask.on(
          'state_changed',
          (snapshot) => {
            const progress = Math.round(
              (snapshot.bytesTransferred / snapshot.totalBytes) * 100
            );
            onProgress?.(progress);
          },
          (error) => {
            console.error('Evidence upload error:', error);
            reject(error);
          },
          async () => {
            try {
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
              console.log('[useDisputeEvidence] Download URL:', downloadURL);
              resolve(downloadURL);
            } catch (err) {
              console.error('Error getting download URL:', err);
              reject(err);
            }
          }
        );
      });
    },
    [user]
  );

  /**
   * Upload multiple evidence files
   *
   * @param {File[]} files - Array of files to upload
   * @param {string} disputeId - Optional dispute ID
   * @returns {Promise<string[]>} - Array of download URLs
   */
  const uploadEvidence = useCallback(
    async (files, disputeId) => {
      if (!user) {
        setError('Must be logged in to upload');
        return [];
      }

      // Check limit
      const totalAfterUpload = evidenceUrls.length + files.length;
      if (totalAfterUpload > MAX_EVIDENCE_IMAGES) {
        setError(`Maximum ${MAX_EVIDENCE_IMAGES} images allowed`);
        return [];
      }

      setUploading(true);
      setError(null);
      setUploadProgress({});

      try {
        const urls = await Promise.all(
          files.map((file, index) =>
            uploadSingleEvidence(file, disputeId, (progress) => {
              setUploadProgress((prev) => ({
                ...prev,
                [index]: progress,
              }));
            })
          )
        );

        setEvidenceUrls((prev) => [...prev, ...urls]);
        return urls;
      } catch (err) {
        setError(err.message || 'Failed to upload evidence');
        return [];
      } finally {
        setUploading(false);
        setUploadProgress({});
      }
    },
    [user, evidenceUrls.length, uploadSingleEvidence]
  );

  /**
   * Remove an evidence image by URL
   */
  const removeEvidence = useCallback((url) => {
    setEvidenceUrls((prev) => prev.filter((u) => u !== url));
  }, []);

  /**
   * Clear all evidence
   */
  const clearEvidence = useCallback(() => {
    setEvidenceUrls([]);
    setError(null);
  }, []);

  /**
   * Check if more images can be added
   */
  const canAddMore = evidenceUrls.length < MAX_EVIDENCE_IMAGES;
  const remainingSlots = MAX_EVIDENCE_IMAGES - evidenceUrls.length;

  return {
    uploadEvidence,
    removeEvidence,
    clearEvidence,
    uploading,
    evidenceUrls,
    error,
    uploadProgress,
    canAddMore,
    remainingSlots,
    maxImages: MAX_EVIDENCE_IMAGES,
  };
}

export default useDisputeEvidence;
